import React from "react";
import {
  Box,
  Button,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CustomButton from "../CustomButton";
import CustomTextField from "../CustomTextField";
import CustomLabel from "../CustomLabel";
import { COLORS } from "../../assets/theme";
import type { InvoiceItem } from "../invoices/InvoiceItemsEditor";

interface PurchaseItemsEditorProps {
  value: InvoiceItem[] | undefined;
  onChange: (items: InvoiceItem[]) => void;
  vatValue?: number | string;
  onVatChange?: (value: number) => void;
  discountValue?: number | string;
  onDiscountChange?: (value: number) => void;
  promoDiscountValue?: number | string;
  onPromoDiscountChange?: (value: number) => void;
  promoValue?: number | string;
  onPromoValueChange?: (value: number) => void;
  isEditMode?: boolean;
  servicesList?: any[];
  onAddProductClick?: () => void;
}

type ServiceOption = {
  value: string | number;
  id?: string;
  name: string;
  price: number;
  type?: string;
};

const clampPercentage = (value: number) => Math.min(100, Math.max(0, value));

const computeItemTotal = (
  item: Pick<InvoiceItem, "quantity" | "unitCost" | "discount">
) => {
  const qty = Number(item.quantity) || 0;
  const unit = Number(item.unitCost) || 0;
  const discountPercent = clampPercentage(Number(item.discount) || 0);
  const subtotal = qty * unit;
  const discountAmount = (subtotal * discountPercent) / 100;
  return Math.round((subtotal - discountAmount) * 100) / 100;
};

const DEFAULT_SERVICE_OPTIONS: ServiceOption[] = [
  { value: 60, name: "60", price: 20 },
  { value: 90, name: "90", price: 30 },
  { value: 120, name: "120", price: 40 },
];

const normalizeServiceOption = (service: any): ServiceOption | null => {
  if (!service) return null;

  const idCandidate =
    service?.id ?? service?._id ?? service?.serviceId ?? service?.service_id;
  const id =
    idCandidate === undefined || idCandidate === null
      ? undefined
      : String(idCandidate);

  const name = String(
    service?.name ??
      service?.title ??
      service?.service_name ??
      service?.label ??
      service?.description ??
      ""
  ).trim();

  const price = Number(
    service?.price ??
      service?.amount ??
      service?.unitPrice ??
      service?.unit_cost ??
      service?.serviceprice ??
      service?.servicePrice ??
      0
  );

  const type = String(
    service?.type ??
      service?.product_type ??
      service?.productType ??
      service?.packageType ??
      service?.package_type ??
      ""
  ).trim();

  if (id) {
    return {
      value: id,
      id,
      name: name || id,
      price: Number.isFinite(price) ? price : 0,
      ...(type ? { type } : {}),
    };
  }

  if (name) {
    return {
      value: name,
      name,
      price: Number.isFinite(price) ? price : 0,
      ...(type ? { type } : {}),
    };
  }

  return null;
};

const PurchaseItemsEditor: React.FC<PurchaseItemsEditorProps> = ({
  value,
  onChange,
  vatValue = 0,
  onVatChange,
  discountValue = 0,
  onDiscountChange,
  isEditMode = false,
  servicesList = [],
  onAddProductClick,
}) => {
  const ensureItemArray = (incoming: any): InvoiceItem[] =>
    Array.isArray(incoming) ? incoming : [];

  const [localItems, setLocalItems] = React.useState<InvoiceItem[]>(
    ensureItemArray(value)
  );

  const baseServiceOptions = React.useMemo<ServiceOption[]>(() => {
    const list = Array.isArray(servicesList) ? servicesList : [];
    const options = list
      .map((service) => normalizeServiceOption(service))
      .filter(Boolean) as ServiceOption[];

    if (options.length) return options;

    // If services were provided but none could be normalized, don't fall back.
    if (Array.isArray(servicesList) && servicesList.length > 0) return [];

    // Allow fallback only when no services are provided and not in edit mode.
    return isEditMode ? [] : DEFAULT_SERVICE_OPTIONS;
  }, [servicesList, isEditMode]);

  const serviceOptions = React.useMemo<ServiceOption[]>(() => {
    const options: ServiceOption[] = [...baseServiceOptions];
    const existing = new Set(options.map((opt) => String(opt.value)));

    localItems.forEach((it) => {
      const optionValue = (it.serviceId ?? it.service) as string | number;
      const key = String(optionValue ?? "");
      if (!key) return;
      if (existing.has(key)) return;
      existing.add(key);
      options.push({
        value: optionValue,
        id: it.serviceId,
        name: it.serviceLabel || key,
        price: Number(it.unitCost) || 0,
      });
    });

    return options;
  }, [baseServiceOptions, localItems]);

  React.useEffect(() => {
    setLocalItems(ensureItemArray(value));
  }, [value]);

  React.useEffect(() => {
    if (localItems.length === 0) {
      handleAddItem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localItems.length]);

  const updateItems = (newItems: InvoiceItem[]) => {
    setLocalItems(newItems);
    onChange(newItems);
  };

  const handleAddItem = () => {
    const nextId = localItems.length
      ? Math.max(...localItems.map((it) => it.id)) + 1
      : 1;

    const firstOption =
      Array.isArray(baseServiceOptions) && baseServiceOptions.length > 0
        ? baseServiceOptions[0]
        : DEFAULT_SERVICE_OPTIONS[0];

    const newItem: InvoiceItem = {
      id: nextId,
      service: firstOption.value,
      serviceId:
        firstOption.id ||
        (typeof firstOption.value === "string" ? firstOption.value : undefined),
      serviceLabel: firstOption.name,
      quantity: 1,
      unitCost: Number(firstOption.price) || 0,
      discount: 0,
      totalPrice: computeItemTotal({
        quantity: 1,
        unitCost: Number(firstOption.price) || 0,
        discount: 0,
      }),
    };

    updateItems([...localItems, newItem]);
  };

  const totalAmount = localItems.reduce(
    (sum, item) => sum + (Number(item.totalPrice) || 0),
    0
  );
  const discountPercent = Number(discountValue) || 0;
  const vatPercent = Number(vatValue) || 0;
  const discountAmount = (totalAmount * discountPercent) / 100;
  const amountAfterDiscount = totalAmount - discountAmount;
  const vatAmount = (amountAfterDiscount * vatPercent) / 100;
  const netAmount = Math.round((amountAfterDiscount + vatAmount) * 100) / 100;
  const numberFieldSx = {
    "& input[type=number]": {
      MozAppearance: "textfield",
    },
    "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
      {
        WebkitAppearance: "none",
        margin: 0,
      },
  };

  const compactFieldWidth = "28px";
  const numberFieldWidth = "18px";

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mb: 1 }}>
        {onAddProductClick && (
          <CustomButton
            textColor={COLORS.WhiteText}
            label="Add Product"
            bgColor={COLORS.MainColor}
            iconButtonSize={40}
            startIcon={<AddIcon />}
            onClick={onAddProductClick}
          />
        )}
      </Box>
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Item</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Quantity</TableCell>
              <TableCell>Unit Cost</TableCell>
              <TableCell>Discount (%)</TableCell>
              <TableCell>Total Price</TableCell>
              <TableCell align="center" sx={{ width: 110 }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {localItems.map((item) => {
              const selectedOption = serviceOptions.find(
                (opt) => String(opt.value) === String(item.service)
              );

              return (
                <TableRow key={item.id}>
                  <TableCell>
                    <Select
                      size="small"
                      value={item.service}
                      onChange={(e) => {
                        const rawValue = e.target.value as any;
                        const picked = serviceOptions.find(
                          (opt) => String(opt.value) === String(rawValue)
                        );

                        const nextValue = picked ? picked.value : rawValue;

                        const updatedItems = localItems.map((it) => {
                          if (it.id !== item.id) return it;
                          const updated: InvoiceItem = {
                            ...it,
                            service: nextValue,
                          };

                          if (picked) {
                            updated.serviceId =
                              picked.id ||
                              (typeof picked.value === "string"
                                ? picked.value
                                : undefined);
                            updated.serviceLabel = picked.name;
                            updated.quantity = 1;
                            updated.unitCost = picked.price || 0;
                            updated.discount = 0;
                          }

                          updated.totalPrice = computeItemTotal(updated);
                          return updated;
                        });
                        updateItems(updatedItems);
                      }}
                      sx={{ minWidth: 150 }}
                    >
                      {serviceOptions.map((option) => (
                        <MenuItem key={String(option.value)} value={option.value}>
                          {`${option.name} - $${option.price}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell>
                  <CustomTextField
                    value={selectedOption?.type || "-"}
                    onChange={() => {}}
                    minWidth={compactFieldWidth}
                    size="small"
                    disabled
                  />
                </TableCell>
                  <TableCell>
                  <CustomTextField
                    value={item.quantity}
                    type="number"
                    onChange={(e) => {
                        const value = Number(e.target.value);
                        const updatedItems = localItems.map((it) => {
                          if (it.id !== item.id) return it;
                          const updated = {
                            ...it,
                            quantity: value,
                          } as InvoiceItem;
                          updated.totalPrice = computeItemTotal(updated);
                          return updated;
                        });
                        updateItems(updatedItems);
                    }}
                    minWidth={numberFieldWidth}
                    size="small"
                    sx={numberFieldSx}
                  />
                </TableCell>
                <TableCell>
                  <CustomTextField
                    value={item.unitCost}
                    type="number"
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        const updatedItems = localItems.map((it) => {
                          if (it.id !== item.id) return it;
                          const updated = {
                            ...it,
                            unitCost: value,
                          } as InvoiceItem;

                          const matchedOption = baseServiceOptions.find(
                            (opt) => Number(opt.price) === value
                          );

                          if (matchedOption) {
                            updated.service = matchedOption.value;
                            updated.serviceId =
                              matchedOption.id ||
                              (typeof matchedOption.value === "string"
                                ? matchedOption.value
                                : undefined);
                            updated.serviceLabel = matchedOption.name;
                          }

                          updated.totalPrice = computeItemTotal(updated);
                          return updated;
                        });
                        updateItems(updatedItems);
                    }}
                    minWidth={numberFieldWidth}
                    size="small"
                    sx={numberFieldSx}
                  />
                </TableCell>
                <TableCell>
                  <CustomTextField
                    value={item.discount}
                    type="number"
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        const updatedItems = localItems.map((it) => {
                          if (it.id !== item.id) return it;
                          const updated = {
                            ...it,
                            discount: clampPercentage(value),
                          } as InvoiceItem;
                          updated.totalPrice = computeItemTotal(updated);
                          return updated;
                        });
                        updateItems(updatedItems);
                      }}
                    minWidth={numberFieldWidth}
                    size="small"
                    sx={numberFieldSx}
                    inputProps={{ step: 0.01, min: 0, max: 100 }}
                  />
                </TableCell>
                <TableCell>
                  <CustomTextField
                    value={item.totalPrice}
                    type="number"
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        const updatedItems = localItems.map((it) =>
                          it.id === item.id ? { ...it, totalPrice: value } : it
                        );
                        updateItems(updatedItems);
                    }}
                    minWidth={numberFieldWidth}
                    size="small"
                    sx={numberFieldSx}
                  />
                </TableCell>
                <TableCell align="center">
                  <Button
                    color="error"
                    size="small"
                    onClick={() => {
                      const updatedItems = localItems.filter(
                        (it) => it.id !== item.id
                      );
                      updateItems(updatedItems);
                    }}
                    sx={{ minWidth: 0, padding: 0 }}
                  >
                    <DeleteIcon fontSize="small" />
                  </Button>
                  <Button
                    size="small"
                    onClick={handleAddItem}
                    sx={{
                      minWidth: 0,
                      ml: 0.75,
                      width: 26,
                      height: 26,
                      borderRadius: "50%",
                      backgroundColor: COLORS.MainColor,
                      color: "#fff",
                      "&:hover": { backgroundColor: COLORS.MainColor },
                    }}
                  >
                    <AddIcon fontSize="small" />
                  </Button>
                </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ mt: 2 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            columnGap: 2,
            rowGap: 1.5,
            justifyItems: "stretch",
            alignItems: "center",
          }}
        >
          {/* Row 1: Subtotal */}
          <Box />
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
            }}
          >
            <CustomLabel text="Subtotal" />
            <CustomLabel
              text={`${totalAmount.toFixed(2)}$`}
              fontWeight="bold"
            />
          </Box>

          {/* Row 2: Discount */}
          <Box />
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
            }}
          >
            <CustomLabel text="Discount" />
            <CustomLabel text="%" />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {isEditMode ? (
                <CustomTextField
                  value={discountPercent}
                  type="number"
                  onChange={(e) =>
                    onDiscountChange?.(
                      clampPercentage(Number(e.target.value) || 0)
                    )
                  }
                  size="small"
                  sx={{ width: 80 }}
                  inputProps={{ step: 0.01, min: 0, max: 100 }}
                />
              ) : (
                <CustomLabel text={`${discountPercent}%`} />
              )}
            </Box>
          </Box>

          {/* Row 3: (empty) | VAT (aligned under Discount) */}
          <Box />
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
            }}
          >
            <CustomLabel text="VAT" />
            <CustomLabel text="%" />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {isEditMode ? (
                <CustomTextField
                  value={vatPercent}
                  type="number"
                  onChange={(e) =>
                    onVatChange?.(clampPercentage(Number(e.target.value) || 0))
                  }
                  size="small"
                  sx={{ width: 80 }}
                  inputProps={{ step: 0.01, min: 0, max: 100 }}
                />
              ) : (
                <CustomLabel text={`${vatPercent}%`} />
              )}
            </Box>
          </Box>

          {/* Row 4: empty | Net Amount */}
          <Box sx={{ display: { xs: "none", sm: "block" } }} />
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderTop: "1px solid #e0e0e0",
              pt: 1,
              mt: 0.5,
            }}
          >
            <CustomLabel text="Net Amount" fontWeight="bold" />
            <CustomLabel text={`${netAmount.toFixed(2)}$`} fontWeight="bold" />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PurchaseItemsEditor;
